import {
  add,
  allocate,
  dinero,
  down,
  equal,
  greaterThan,
  greaterThanOrEqual,
  halfDown,
  halfEven,
  halfUp,
  isNegative,
  isPositive,
  isZero,
  lessThan,
  lessThanOrEqual,
  multiply,
  subtract,
  toDecimal,
  toSnapshot,
  toUnits,
  transformScale,
  up
} from 'dinero.js'
import { USD } from 'dinero.js/currencies'
import numeral from 'numeral'

import country from '@/utilities/country.js'

// #region - Numeral locales
if (country.is('ro')) {
  numeral.register('locale', 'ro', {
    delimiters: {
      thousands: '.',
      decimal: ','
    },
    currency: {
      symbol: ' lei'
    }
  })
}

numeral.locale(country.get())

export const baseNumeral = numeral
export const DEFAULT_CURRENCY = import.meta.env.VITE_CURRENCY ?? 'TRY'
export const DEFAULT_SCALE = parseInt(import.meta.env.VITE_CURRENCY_EXPONENT, 10) ?? 2
// #endregion

const exponent = parseInt(import.meta.env.VITE_CURRENCY_EXPONENT, 10)

const currencies = {
  TRY: {
    code: 'TRYC',
    base: 10,
    exponent: exponent
  },
  RON: {
    code: 'RONC',
    base: 10,
    exponent: exponent
  },
  USD: USD
}

const wrapper = instance => {
  return {
    ...instance,
    toFormat: format => {
      return numeral(parseFloat(wrapper(instance).getAmount())).format(format)
    },
    getFormattedAmount: () => {
      return wrapper(instance).getTrimmedFormattedAmount()
    },
    getTrimmedFormattedAmount: () => {
      const [decimal, fractional] = toUnits(instance)

      const snapshot = toSnapshot(instance)
      const currencyMap = { TRYC: 'TRY', RONC: 'RON', USDC: 'USD' }
      const currencyCode = currencyMap[snapshot.currency.code] || snapshot.currency.code

      return new Intl.NumberFormat(country.get(), {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: fractional !== 0 ? snapshot.scale : 0,
        maximumFractionDigits: fractional !== 0 ? snapshot.scale : 0
      }).format(parseFloat(wrapper(instance).getAmount()))
    },
    getFormattedAmountWithoutSymbol: () => {
      const [decimal, fractional] = toUnits(instance)

      if (fractional !== 0) {
        return numeral(parseFloat(wrapper(instance).getAmount())).format('0.00[,]00')
      }

      return numeral(decimal).format('0[,]000')
    },
    getAmount: () => {
      return toDecimal(instance, ({ value, currency }) => {
        return Number(value).toFixed(toSnapshot(instance).scale)
      })
    },
    getMinorAmount: () => {
      return toDecimal(multiply(instance, 10 ** toSnapshot(instance).scale), ({ value, currency }) => {
        return Number(value)
      })
    },
    negated: () => {
      return wrapper(multiply(instance, -1))
    },
    snapshot: () => {
      return toSnapshot(instance)
    },
    toScale: scale => {
      return wrapper(transformScale(instance, scale))
    },
    plus: amount => {
      return wrapper(add(instance, amount))
    },
    minus: amount => {
      return wrapper(subtract(instance, amount))
    },
    multiply: (multiplier, roundingMode = null) => {
      const [integer, decimal] = multiplier.toString().split('.')
      let amount = multiplier * 10 ** (decimal ? decimal.length : 0)

      amount = parseInt(parseFloat(amount).toFixed(0), 10)

      const result = multiply(instance, { amount: amount, scale: decimal ? decimal.length : 0 })

      if (roundingMode !== null) {
        return wrapper(transformScale(result, toSnapshot(instance).scale, roundingMode))
      }

      return wrapper(transformScale(result, toSnapshot(instance).scale))
    },
    divide: (divider, roundingMode = null) => {
      const scale = toSnapshot(instance).scale

      const [d1, d2] = allocate(instance, [
        { amount: 1, scale: scale },
        { amount: divider - 1, scale: scale }
      ])

      if (roundingMode !== null) {
        return wrapper(transformScale(d1, toSnapshot(instance).scale, roundingMode))
      }

      return wrapper(transformScale(d1, toSnapshot(instance).scale))
    },
    roundUp: () => {
      return wrapper(transformScale(instance, 0, up))
    },
    isEqualTo: amount => {
      return equal(instance, amount)
    },
    isNotEqualTo: amount => {
      return !equal(instance, amount)
    },
    isGreaterThan: amount => {
      return greaterThan(instance, amount)
    },
    isGreaterThanOrEqualTo: amount => {
      return greaterThanOrEqual(instance, amount)
    },
    isLessThan: amount => {
      return lessThan(instance, amount)
    },
    isLessThanOrEqualTo: amount => {
      return lessThanOrEqual(instance, amount)
    },
    isZero: () => {
      return isZero(instance)
    },
    isNotZero: () => {
      return !isZero(instance)
    },
    isPositive: () => {
      return isPositive(instance)
    },
    isNegative: () => {
      return isNegative(instance)
    },
    isPositiveOrZero: () => {
      return isPositive(instance) || isZero(instance)
    },
    isNegativeOrZero: () => {
      return isNegative(instance) || isZero(instance)
    }
  }
}

export const RoundingMode = {
  UP: up,
  DOWN: down,
  HALF_UP: halfUp,
  HALF_DOWN: halfDown,
  HALF_EVEN: halfEven
}

export default {
  of: (amount, currency = null, scale = null) => {
    if (amount === null || amount === undefined) {
      amount = 0
    }

    if (currency !== null) {
      currency = currencies[currency]
    } else {
      currency = currencies[import.meta.env.VITE_CURRENCY]
    }

    const power = 10 ** (scale ?? exponent)
    const minorAmount = Math.round(amount * power)

    return wrapper(dinero({ amount: minorAmount, currency: currency, scale: scale ?? exponent }))
  },
  ofMinor: (amount, currency = null, scale = null) => {
    if (amount === null || amount === undefined) {
      amount = 0
    }

    if (currency !== null) {
      currency = currencies[currency]
    } else {
      currency = currencies[import.meta.env.VITE_CURRENCY]
    }

    return wrapper(dinero({ amount: amount, currency: currency, scale: scale ?? exponent }))
  }
}
